/**
 * LOADED PHOTO SCREEN
 *
 * After seller signature is complete, the driver takes a photo of the item
 * loaded and secured in their vehicle before departing to the buyer.
 * Uses expo-image-picker launchCameraAsync for photo capture.
 */
import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Alert,
  ActivityIndicator, Platform, Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { spacing, radii, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';

/* ================================================================= */

// Convert a picked blob:/file uri to a self-contained base64 data: URI so it survives the
// iOS PWA picker-triggered tab reload (which revokes blob: URIs + wipes React state).
async function toDataUri(uri: string): Promise<string> {
  try {
    if (uri.startsWith('data:')) return uri;
    const resp = await fetch(uri);
    const blob = await resp.blob();
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch { return uri; }
}

export function LoadedPhotoScreen({ route, navigation }: any) {
  const { jobId } = route.params;
  const PERSIST_KEY = `cp_loaded_${jobId}`;

  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [restored, setRestored] = useState(false);

  // Restore a captured photo after a picker-triggered reload, then persist on change.
  useEffect(() => {
    AsyncStorage.getItem(PERSIST_KEY).then(v => { if (v) setPhotoUri(v); setRestored(true); }).catch(() => setRestored(true));
  }, [jobId]);
  useEffect(() => {
    if (!restored) return;
    if (photoUri && photoUri.startsWith('data:')) AsyncStorage.setItem(PERSIST_KEY, photoUri).catch(() => {});
  }, [restored, photoUri]);

  /* ---- take / retake photo ---- */
  const handleTakePhoto = async () => {
    // Library picker first (offers Take Photo + Choose from Library on mobile web/iOS) so a
    // driver whose camera launch fails isn't hard-stopped from finishing the loaded-photo step.
    try {
      const lib = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 0.6 });
      if (!lib.canceled && lib.assets?.[0]) { setPhotoUri(await toDataUri(lib.assets[0].uri)); return; }
      if (lib.canceled) return;
    } catch {}
    try {
      const cam = await ImagePicker.launchCameraAsync({ quality: 0.6, allowsEditing: false });
      if (!cam.canceled && cam.assets?.[0]) setPhotoUri(await toDataUri(cam.assets[0].uri));
    } catch {
      Alert.alert('Could not add photo', 'Please allow photo/camera access for this site in your browser settings, then try again.');
    }
  };

  /* ---- submit ---- */
  const handleSubmit = async () => {
    if (!photoUri) {
      Alert.alert('Photo Required', 'Please take a photo of the loaded item before submitting.');
      return;
    }

    setSubmitting(true);
    const fileName = `loaded-${Date.now()}.jpg`;
    // Downscale on web so the upload is small + reliable on weak field signal (a full-res iPhone
    // photo → ~0.4MB). Same fix as ChecklistScreen — this step was single-attempt + full-res and
    // stranded drivers at the "loaded" step on bad signal.
    const prepBlob = async (): Promise<Blob | null> => {
      let url: string | null = null;
      try {
        const src = await (await fetch(photoUri)).blob();
        if (!src.type.startsWith('image/')) return src;
        url = URL.createObjectURL(src);
        // `Image` is React Native's component here (see the react-native import above), not
        // the browser constructor it shadows - `new Image()` threw and this downscale never
        // ran once. Same bug as ChecklistScreen.
        const img: HTMLImageElement = await new Promise((res, rej) => { const im = new (window as any).Image(); im.onload = () => res(im); im.onerror = () => rej(new Error('img')); im.src = url as string; });
        const MAX = 1600; let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
        if (!w || !h) return src;
        if (Math.max(w, h) > MAX) { const sc = MAX / Math.max(w, h); w = Math.round(w * sc); h = Math.round(h * sc); }
        const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d'); if (!ctx) return src;
        ctx.drawImage(img, 0, 0, w, h);
        return await new Promise<Blob | null>((r) => canvas.toBlob((b) => r(b), 'image/jpeg', 0.6));
      } catch { return null; } finally { if (url) URL.revokeObjectURL(url); }
    };
    const buildForm = async (): Promise<FormData> => {
      const fd = new FormData();
      if (Platform.OS === 'web' && (photoUri.startsWith('data:') || photoUri.startsWith('blob:'))) {
        const blob = (await prepBlob()) || await (await fetch(photoUri)).blob();
        fd.append('photo', blob, fileName);
      } else {
        fd.append('photo', { uri: photoUri, type: 'image/jpeg', name: fileName } as any);
      }
      return fd;
    };
    // Retry with a fail-fast timeout; on WEB do NOT set the multipart Content-Type ourselves —
    // the browser must add the boundary or the server can't parse the body and the request hangs.
    let ok = false, lastErr: any = null;
    for (let attempt = 0; attempt < 3 && !ok; attempt++) {
      try {
        await api.post(`/jobs/${jobId}/loaded-photo`, await buildForm(), {
          headers: Platform.OS === 'web' ? undefined : { 'Content-Type': 'multipart/form-data' },
          timeout: 75000,
        });
        ok = true;
      } catch (e: any) { lastErr = e; if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); }
    }
    if (!ok) {
      console.warn('Loaded photo upload failed after retries:', lastErr?.message);
      setSubmitting(false);
      Alert.alert('Upload Failed', 'We could not upload the loaded photo. Your photo is saved on this device — please check your connection and tap Submit again.');
      return; // do NOT advance on failure
    }
    setSubmitting(false);
    AsyncStorage.removeItem(PERSIST_KEY).catch(() => {}); // uploaded — clear the snapshot

    navigation.navigate('ActiveJob', {
      completedAction: 'LOADED_PHOTO_COMPLETE',
      jobId,
    });
  };

  /* ================================================================= */

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Loaded & Secured</Text>
          <Text style={styles.headerSub}>One photo before you drive off</Text>
        </View>
        <View style={{ width: 22 }} />
      </View>

      <View style={styles.content}>
        {/* Info card */}
        <View style={styles.infoCard}>
          <Ionicons name="information-circle-outline" size={18} color={RD.info} />
          <Text style={styles.infoText}>
            Show the item strapped down and secured in your vehicle. This protects you and proves
            the item left pickup undamaged.
          </Text>
        </View>

        {/* Camera card — empty state vs. photo saved */}
        <View style={styles.cameraCard}>
          {photoUri ? (
            <View style={styles.previewWrapper}>
              <Image source={{ uri: photoUri }} style={styles.preview} resizeMode="cover" />
              <View style={styles.savedPill}>
                <Ionicons name="checkmark" size={12} color={RD.money} />
                <Text style={styles.savedPillText}>Photo saved</Text>
              </View>
              <TouchableOpacity style={styles.retakeBtn} onPress={handleTakePhoto} activeOpacity={0.85}>
                <Ionicons name="camera-outline" size={17} color="#fff" />
                <Text style={styles.retakeBtnText}>Retake</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity style={styles.cameraEmpty} onPress={handleTakePhoto} activeOpacity={0.8}>
              <View style={styles.cameraIconWrap}>
                <Ionicons name="camera" size={36} color="#fff" />
              </View>
              <Text style={styles.cameraBtnTitle}>Take secured-load photo</Text>
              <Text style={styles.cameraBtnSub}>Tap to open the camera</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Continue — gated on photo */}
        <TouchableOpacity
          style={[styles.submitBtn, (!photoUri || submitting) && styles.btnDisabled]}
          onPress={handleSubmit}
          disabled={!photoUri || submitting}
          activeOpacity={0.85}
        >
          {submitting ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.submitText}>
              {photoUri ? 'Start Driving to Delivery' : 'Take a photo to continue'}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

/* ================================================================= */

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: RD.bg },

  // Header
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingTop: Platform.OS === 'ios' ? 56 : 36,
    paddingBottom: spacing.md,
    backgroundColor: RD.surface,
    borderBottomWidth: 1, borderBottomColor: RD.border,
  },
  backBtn: { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerCenter: { flex: 1, alignItems: 'center' },
  headerTitle: { fontSize: 17, fontWeight: '700', color: RD.text },
  headerSub: { fontSize: 12, color: RD.textFaint, marginTop: 2 },

  content: { flex: 1, padding: spacing.lg, gap: spacing.base },

  // Info card
  infoCard: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 11,
    backgroundColor: RD.infoSoft,
    borderRadius: 18, padding: 14,
    borderWidth: 1, borderColor: RD.info + '25',
  },
  infoText: { flex: 1, fontSize: 12.5, color: RD.textSec, lineHeight: 18 },

  // Camera card container
  cameraCard: {
    flex: 1, minHeight: 360,
    backgroundColor: RD.surface,
    borderRadius: 18,
    borderWidth: 1, borderColor: RD.border,
    overflow: 'hidden',
    shadowColor: '#000', shadowOpacity: 0.03, shadowRadius: 8, elevation: 1,
  },

  // Camera empty state
  cameraEmpty: {
    flex: 1,
    backgroundColor: RD.g03,
    justifyContent: 'center', alignItems: 'center',
    gap: 12, padding: spacing.xl,
  },
  cameraIconWrap: {
    width: 76, height: 76, borderRadius: 38,
    backgroundColor: RD.ink,
    justifyContent: 'center', alignItems: 'center',
  },
  cameraBtnTitle: {
    fontSize: 16, fontWeight: '700', color: RD.text,
    fontFamily: fonts.semiBold,
  },
  cameraBtnSub: {
    fontSize: 13, color: RD.textFaint, textAlign: 'center',
  },

  // Photo saved state
  previewWrapper: { flex: 1, position: 'relative' },
  preview: { flex: 1, backgroundColor: RD.g10 },
  savedPill: {
    position: 'absolute', top: 12, left: 12,
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: RD.moneySoft, borderRadius: radii.full,
    paddingHorizontal: 10, paddingVertical: 5,
  },
  savedPillText: { fontSize: 12, fontWeight: '700', color: RD.money },
  retakeBtn: {
    position: 'absolute', bottom: 12, alignSelf: 'center',
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    height: 48, paddingHorizontal: 20,
    backgroundColor: 'rgba(20,16,12,0.7)', borderRadius: 12,
  },
  retakeBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },

  // Continue CTA
  submitBtn: {
    backgroundColor: RD.ink, borderRadius: 18, height: 60,
    flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8,
  },
  submitText: { color: '#fff', fontWeight: '700', fontSize: 17, fontFamily: fonts.semiBold },
  btnDisabled: { opacity: 0.5 },
});
